/* eslint-disable @typescript-eslint/no-explicit-any */
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Stack,
    AlertTitle,
    Box,
} from "@mui/material"
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import { ReactElement, SyntheticEvent, useContext, useState } from "react"
import { Control, Controller } from "react-hook-form"

import { Datasets, LCTOnlyDatasets } from "../../../../constants/enums"
import { SUPPLEMENTING_INFO_KW } from "../../../../constants/generalConstants"
import {
    SINGLE_DEMAND_DEVICE_NAV_LINK,
    SINGLE_GEN_DEVICE_NAV_LINK,
} from "../../../../router/routes"
import useSearchLct from "../../../../services/middleware/hooks/singleApplication/useSearchLct"
import { IDeviceSearchResult } from "../../../../services/middleware/types/singleApplication/searchLctTypes"
import { ITEM_BORDER_MULTIPLIER } from "../../../../themes/defaultThemeParams"
import { noOptionsText, snackbar } from "../../../../utils/general/generalUtils"
import AppButton from "../../../buttons/appButton"
import AppAlert from "../../../dataDisplay/alerts/appAlert"
import VerticalKeyValueList from "../../../dataDisplay/verticalKeyValueList"
import DisplayIf from "../../../wrappers/displayIf"
import { ApplicationContext } from "../applicationProvider"
import { SupplyPhases } from "../types/formGeneratorTypes"

export const SUPPLEMENTING_KEY = "deviceSysRefSearchResult"
const INPUT_EVENT_TYPE = "input"

interface ISelectNewDeviceProps {
    registerName: string
    control: Control
    programmaticallySetForm: (key: string, value: any) => void
    watch: (key: string) => any
    setClass: () => void
    deviceType: string
    dataset: Datasets
    supplementingInfoKey: string
    phases?: SupplyPhases
}

/**
 * Renders a device search field that allows the user to search for a device
 * The user can then select a device from the autocomplete suggestions
 */
const SelectNewDevice = ({
    registerName,
    control,
    programmaticallySetForm,
    watch,
    setClass,
    deviceType,
    dataset,
    supplementingInfoKey,
    phases,
}: ISelectNewDeviceProps): ReactElement => {
    // Extract initial value from supplementing info
    const defaultFormValue =
        watch(supplementingInfoKey)?.[SUPPLEMENTING_KEY] ?? undefined

    const { jurisdiction } = useContext(ApplicationContext)

    // Set initial value only on first render
    const [defaultValue] = useState(() => defaultFormValue)
    const [searchTerm, setSearchTerm] = useState("")
    const [displayDetails, setDisplayDetails] =
        useState<IDeviceSearchResult | null>(defaultFormValue)
    const { data, isLoading, isError } = useSearchLct(
        searchTerm,
        deviceType,
        dataset,
        jurisdiction,
        phases
    )

    /** Called when user types in the address search field to fetch suggestions */
    const handleSearch = (
        _event: SyntheticEvent<Element, Event>,
        value: string,
        reason: string
    ): void => {
        // Only fire API when user is typing
        if (value && reason === INPUT_EVENT_TYPE) {
            setSearchTerm(value)
        }
    }

    /** Called when user selects an address from the autocomplete suggestions */
    const handleSelect = (
        _event: SyntheticEvent<Element, Event>,
        value: IDeviceSearchResult | null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formSet: (...event: any[]) => void
    ): void => {
        if (value !== null) {
            formSet(value.deviceRef)
            setClass()
        } else {
            formSet(null)
        }
        setDisplayDetails(value)
        // TODO, make it an update
        programmaticallySetForm(SUPPLEMENTING_INFO_KW, {
            [SUPPLEMENTING_KEY]: value,
        })
    }

    /** Routes user to single device summary page */
    const navigateToSingleDevice = (): void => {
        if (typeof displayDetails?.deviceRef === "string") {
            if (dataset === Datasets.TTR) {
                window.open(
                    SINGLE_GEN_DEVICE_NAV_LINK(displayDetails.deviceRef),
                    "_blank"
                )
            } else {
                window.open(
                    SINGLE_DEMAND_DEVICE_NAV_LINK(
                        dataset as unknown as LCTOnlyDatasets,
                        displayDetails.deviceRef
                    ),
                    "_blank"
                )
            }
        } else {
            snackbar(
                "Something went wrong! It was not possible to take you to the device summary page",
                "warning"
            )
        }
    }

    const requiredRule = {
        required: { value: true, message: "Please select an option" },
    }

    return (
        <Stack spacing={4} width="100%" paddingBottom={2}>
            <Controller
                name={registerName}
                rules={requiredRule}
                control={control}
                render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                        defaultValue={defaultValue}
                        options={data ?? []}
                        filterOptions={(options) => options}
                        loading={isLoading}
                        isOptionEqualToValue={(option, value) =>
                            option.deviceRef === value.deviceRef
                        }
                        getOptionLabel={(option) => option.displayValue}
                        onChange={(e, v) => handleSelect(e, v, field.onChange)}
                        onInputChange={handleSearch}
                        noOptionsText={noOptionsText(
                            searchTerm,
                            "Start typing to search for a device...",
                            "Continue typing to search for a device...",
                            "No devices found"
                        )}
                        renderOption={(props, option, { inputValue }) => {
                            const matches = match(
                                String(option.displayValue),
                                inputValue,
                                {
                                    insideWords: true,
                                }
                            )
                            const parts = parse(
                                String(option.displayValue),
                                matches
                            )

                            return (
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                <li {...props}>
                                    <div>
                                        {parts.map((part, index) => (
                                            <span
                                                key={
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    part.text +
                                                    part.highlight +
                                                    index
                                                }
                                                style={{
                                                    fontWeight: part.highlight
                                                        ? 700
                                                        : 400,
                                                }}
                                            >
                                                {part.text}
                                            </span>
                                        ))}
                                    </div>
                                </li>
                            )
                        }}
                        renderInput={(params) => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            const {
                                InputLabelProps,
                                InputProps,
                                ...otherParams
                            } = params

                            return (
                                <TextField
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...otherParams}
                                    label="Device Search"
                                    placeholder="Start typing to search for a device..."
                                    error={!!error || isError}
                                    helperText={error?.message}
                                    InputProps={{
                                        ...InputProps,
                                        sx: {
                                            borderRadius:
                                                ITEM_BORDER_MULTIPLIER,
                                        },
                                        endAdornment: (
                                            <>
                                                {isLoading ? (
                                                    <CircularProgress
                                                        color="inherit"
                                                        size={20}
                                                    />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )
                        }}
                    />
                )}
            />
            <DisplayIf condition={watch(registerName)}>
                <AppAlert severity="info">
                    <AlertTitle>Selected Device Details</AlertTitle>
                    <VerticalKeyValueList
                        overrideColour="palette.primary.main"
                        kwargs={{
                            "Device Ref": displayDetails?.deviceRef,
                            "Device Model No": displayDetails?.deviceModelNo,
                            "Number of Phases": displayDetails?.devicePhases,
                            Manufacturer: displayDetails?.deviceManufacturer,
                            [String(
                                displayDetails?.deviceSizeName
                            )]: `${displayDetails?.deviceSize} ${displayDetails?.deviceSizeUnit}`,
                        }}
                    />
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        flexDirection="row"
                        marginTop={2}
                    >
                        <AppButton
                            endIcon={<NavigateNextIcon />}
                            variant="outlined"
                            onClick={navigateToSingleDevice}
                        >
                            View Device Details
                        </AppButton>
                    </Box>
                </AppAlert>
            </DisplayIf>
        </Stack>
    )
}

SelectNewDevice.defaultProps = {
    phases: 3,
}

export default SelectNewDevice
